import { sessionManager, secureStorage } from './sessionStorage';
import { createClient } from '@supabase/supabase-js';
import { toLocalDate } from '../utils/dateDisplay';

// Clear any legacy localStorage data on app load
// Note: We're NOT clearing Supabase auth tokens anymore - we need those for persistence
// clearLegacyStorage();

// Create a singleton Supabase instance with standard localStorage persistence
// This allows the session to persist across browser restarts
let supabaseInstance = null;

export const supabase = (() => {
  if (!supabaseInstance) {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error('Missing Supabase environment variables');
    }

    // Standard Supabase configuration with localStorage persistence
    // This is the recommended approach for SPAs
    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,  // Auto-refresh tokens before they expire
        persistSession: true,    // Persist session in localStorage
        detectSessionInUrl: true // For OAuth and magic link flows
      }
    });
  }
  return supabaseInstance;
})();

// Re-export session management utilities
export { sessionManager, tokenStore, checkSessionHealth } from './sessionStorage';

// Helper function to get current user from cache (for quick access)
export const getCurrentUser = () => {
  return sessionManager.getUser() || secureStorage.getUserSession();
};

// Helper function to get current session (from cache)
export const getCurrentSession = async () => {
  const user = getCurrentUser();
  return user ? { user } : null;
};

// Helper to ensure we have a valid session (checks cache)
export const ensureValidSession = async () => {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('No valid session found');
  }
  return user;
};

// Goals helper functions
export const createGoal = async ({ userId, title, category, dueDate, description: _description = null, childAccountId = null, ai_analysis = {} }) => {
  // Extract the string value from category object if it's an object, otherwise use as string
  let categoryValue = category;
  if (typeof category === 'object' && category !== null) {
    categoryValue = category.value || category.id || category;
  }

  const goalData = {
    title,
    category: categoryValue,
    dueDate,
    progress: 0,
    completed: false,
    creationDate: toLocalISOString(getTodayLocal()),
    lastUpdated: toLocalISOString(getTodayLocal()),
    ai_analysis,
  };

  // Add userId only if provided (can be null for child accounts)
  if (userId) {
    goalData.userId = userId;
  }

  // Add childAccountId if provided
  if (childAccountId) {
    goalData.childAccountId = childAccountId;
  }

  const { data, error } = await supabase
    .from('goals')
    .insert([goalData])
    .select();

  if (error) throw error;
  return data;
};

export const updateGoal = async ({ id, updates }) => {
  const { data, error } = await supabase
    .from('goals')
    .update({
      ...updates,
      lastUpdated: new Date().toISOString().split('T')[0], // date type
    })
    .eq('id', id)
    .select();

  if (error) throw error;
  return data;
};

// Tasks helper functions
export const createTask = async ({ userId, goalId, title, weightage, startDate, endDate, recurrence, childAccountId = null, ai_analysis = {} }) => {
  // Set default recurrence pattern if none provided
  const defaultRecurrence = recurrence || { pattern: "none" };

  const taskData = {
    goalId,
    title,
    weightage,
    progress: 0,
    completed: false,
    startDate,
    endDate,
    recurrence: defaultRecurrence,
    creationDate: toLocalISOString(getTodayLocal()),
    ai_analysis,
  };

  // Add userId only if provided (can be null for child accounts)
  if (userId) {
    taskData.userId = userId;
  }

  // Add childAccountId if provided
  if (childAccountId) {
    taskData.childAccountId = childAccountId;
  }

  const { data, error } = await supabase
    .from('tasks')
    .insert([taskData])
    .select();

  if (error) throw error;
  return data;
};

export const updateTask = async ({ id, updates }) => {
  // Get the current task data to compare start dates and recurrence
  const { data: currentTask, error: fetchError } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', id)
    .single();

  if (fetchError) throw fetchError;

  // Check if start date is being changed
  const isStartDateChanged = updates.startDate &&
    new Date(currentTask.startDate).getTime() !== new Date(updates.startDate).getTime();

  // Check if recurrence pattern is being changed
  const isRecurrenceChanged = updates.recurrence &&
    JSON.stringify(currentTask.recurrence) !== JSON.stringify(updates.recurrence);

  // If start date or recurrence changed, archive progress history and reset progress
  if (isStartDateChanged || isRecurrenceChanged) {
    const changeReason = isStartDateChanged && isRecurrenceChanged
      ? 'start_date_and_recurrence_change'
      : isStartDateChanged
        ? 'start_date_change'
        : 'recurrence_change';

    // Handle progress reset and version history
    updates.progress = 0;
    await handleTaskConfigChange(currentTask, updates.startDate || currentTask.startDate, updates.recurrence || currentTask.recurrence, changeReason);
  }
  const { data, error } = await supabase
    .from('tasks')
    .update(updates)
    .eq('id', id)
    .select();

  if (error) throw error;

  // Get all tasks for this goal to pass updated data to recalculateGoalProgress
  const { data: allTasks, error: tasksError } = await supabase
    .from('tasks')
    .select('id, progress, weightage')
    .eq('goalId', currentTask.goalId);

  if (tasksError) {
    console.error('Error fetching tasks for goal progress calculation:', tasksError);
    // Fallback to database fetch in recalculateGoalProgress
    await recalculateGoalProgress(currentTask.goalId);
  } else {
    // Recalculate goal progress with updated task data
    await recalculateGoalProgress(currentTask.goalId, allTasks);
  }

  return data;
};

// Helper function to handle start date or recurrence changes - archives progress history
const handleTaskConfigChange = async (currentTask, newStartDate, newRecurrence, changeReason) => {
  try {
    // Get current task history
    const { data: taskHistory, error: historyError } = await supabase
      .from('task_history')
      .select('*')
      .eq('taskId', currentTask.id)
      .single();

    if (historyError && historyError.code !== 'PGRST116') {
      throw historyError;
    }

    if (taskHistory && taskHistory.progressHistory && taskHistory.progressHistory.length > 0) {
      // Get current progressVersion array or initialize it
      const currentProgressVersions = taskHistory.progressVersion || [];

      // Helper to extract YYYY-MM-DD from any date format
      const toDateOnly = (dateInput) => {
        if (!dateInput) return null;
        // If already YYYY-MM-DD format, return as-is
        if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
          return dateInput;
        }
        // If ISO string with time, extract date part
        if (typeof dateInput === 'string' && dateInput.includes('T')) {
          return dateInput.split('T')[0];
        }
        // If Date object or parseable string, convert to local YYYY-MM-DD
        const d = new Date(dateInput);
        if (isNaN(d.getTime())) return null;
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      };

      // Get today's date as local YYYY-MM-DD
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

      // Filter out any entry from today (we'll add a new one)
      const filteredVersions = currentProgressVersions.filter(v => {
        const versionDate = toDateOnly(v.endDate) || toDateOnly(v.changedAt);
        return versionDate !== today;
      });

      // Find the highest version number to increment
      const latestVersion = currentProgressVersions.length > 0
        ? Math.max(...currentProgressVersions.map(v => v.version))
        : 0;

      // Create new progress version entry with all dates as local YYYY-MM-DD
      const newProgressVersion = {
        version: latestVersion + 1,
        startDate: toDateOnly(currentTask.startDate),
        endDate: today,
        recurrence: currentTask.recurrence,
        changedAt: today,
        changeReason: changeReason,
        progressHistory: [...taskHistory.progressHistory]
      };

      const updatedProgressVersions = [...filteredVersions, newProgressVersion];

      // Update task history: add to progressVersion and clear progressHistory
      const { error: updateError } = await supabase
        .from('task_history')
        .update({
          progressVersion: updatedProgressVersions,
          progressHistory: [] // Reset to empty array
        })
        .eq('taskId', currentTask.id);

      if (updateError) throw updateError;

      // Reset task progress to 0 in the tasks table
      const { error: taskUpdateError } = await supabase
        .from('tasks')
        .update({ progress: 0 })
        .eq('id', currentTask.id);

      if (taskUpdateError) throw taskUpdateError;


      // Recalculate goal progress after task reset
      await recalculateGoalProgress(currentTask.goalId);
    }
  } catch (error) {
    console.error('Error handling task config change:', error);
    throw error;
  }
};

// Helper function to recalculate goal progress after task changes
export const recalculateGoalProgress = async (goalId, updatedTasks = null) => {
  try {

    let tasks;
    if (updatedTasks) {
      // Use the provided updated tasks data
      tasks = updatedTasks;
    } else {
      // Get all tasks for this goal from database
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select('id, progress, weightage')
        .eq('goalId', goalId);

      if (tasksError) throw tasksError;
      tasks = tasksData;
    }

    if (tasks && tasks.length > 0) {
      // Calculate new goal progress using the correct method: progress * (weightage / 100)
      const weightedProgress = tasks.reduce((sum, task) => {
        return sum + (Number(task.progress) || 0) * ((Number(task.weightage) || 0) / 100);
      }, 0);
      const goalProgress = Math.round(weightedProgress);

      // Check if all tasks are at 100%
      const allTasksCompleted = tasks.every(task => (Number(task.progress) || 0) >= 100);

      const updates = {
        progress: goalProgress,
        lastUpdated: toLocalISOString(getTodayLocal())
      };

      if (allTasksCompleted) {
        updates.completed = true;
        updates.completedAt = toLocalISOString(getTodayLocal());
      }

      // Update goal progress
      const { error: goalUpdateError } = await supabase
        .from('goals')
        .update(updates)
        .eq('id', goalId);

      if (goalUpdateError) {
        console.error('❌ Error updating goal progress:', goalUpdateError);
        throw goalUpdateError;
      }
    }
  } catch (error) {
    console.error('Error recalculating goal progress:', error);
    throw error;
  }
};

// Helper functions for date handling
const ensureDate = (date) => {
  if (!date) return new Date();
  if (date instanceof Date) return date;

  // If it's a YYYY-MM-DD string, parse it as local date
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  return new Date(date);
};

// Helper function to get today's date in local timezone without timezone shift
// Get today's date normalized to start of day
const getTodayLocal = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// Helper function to convert date to YYYY-MM-DD format for database (date type)
// Since database uses date type, we just return YYYY-MM-DD string
const toLocalISOString = (date) => {
  if (!date) return null;

  // If already a string in YYYY-MM-DD format, return as-is
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Use ensureDate to be safe with timezone issues
  const dateObj = ensureDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return null;

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};


const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

// Task History helper functions
export const createOrUpdateTaskHistory = async ({ taskId, userId, goalId, progress, comment = null, progressDate = null, markedAsCompleted = null, taskStartDate: _taskStartDate = null, isRecurring = true, userEmail = null, childAccountId = null }) => {
  try {

    // Validate goalId - it's required for task_history
    let resolvedGoalId = goalId;

    // If goalId is not provided, try to fetch it from the task
    if (!resolvedGoalId) {
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select('goalId')
        .eq('id', taskId)
        .single();

      if (taskError) {
        console.error('Error fetching task for goalId:', taskError);
        throw new Error(`Failed to fetch task goalId: ${taskError.message}`);
      }

      if (!taskData || !taskData.goalId) {
        // Try to find goalId through goals table
        const { data: goalData, error: goalError } = await supabase
          .from('goals')
          .select('id')
          .contains('tasks', [{ id: taskId }])
          .limit(1)
          .maybeSingle();

        if (!goalError && goalData?.id) {
          resolvedGoalId = goalData.id;
          // Update the task with the found goalId
          await supabase
            .from('tasks')
            .update({ goalId: resolvedGoalId })
            .eq('id', taskId);
        } else {
          // If still not found, log warning and return null to skip task_history creation
          console.warn(`Task ${taskId} does not have a goalId. Cannot create task history.`);
          return null;
        }
      } else {
        resolvedGoalId = taskData.goalId;
      }
    }

    // Use progressDate if provided (for recurring tasks), otherwise use today
    const targetDate = progressDate ? ensureDate(progressDate) : getTodayLocal();

    // Check if task history already exists (Shared record for all users)
    const { data: existingHistory, error: fetchError } = await supabase
      .from('task_history')
      .select('*')
      .eq('taskId', taskId)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') {
      throw fetchError;
    }


    if (!existingHistory) {
      // Create new shared task history
      const newTaskHistory = {
        id: generateUUID(),
        taskId: taskId,
        goalId: resolvedGoalId,
        progressHistory: ((progress !== null && progress !== undefined && Math.min(100, Math.max(0, progress)) > 0) || (markedAsCompleted === true) || (progress === null && markedAsCompleted === null && isRecurring)) ? [{
          progressDate: toLocalISOString(targetDate),
          progress: (progress !== null && progress !== undefined) ? Math.round(Math.min(100, Math.max(0, progress))) : 0,
          updatedOn: toLocalISOString(targetDate),
          markedAsCompleted: markedAsCompleted !== null ? markedAsCompleted : false,
          updatedBy: userEmail
        }] : [],
        commentHistory: comment && comment.trim() ? [{
          comments: [comment.trim()],
          commentDate: targetDate.toDateString(),
          commentedBy: userEmail
        }] : [],
        progressVersion: [],
        userId: userId,
        childAccountId: childAccountId
      };

      const { data, error } = await supabase
        .from('task_history')
        .insert(newTaskHistory)
        .select()
        .single();

      if (error) {
        console.error('Error creating task history:', error);
        throw error;
      }


      // Update the shared task's progress field and parent goal progress
      if (progress !== null && progress !== undefined) {
        const cappedProgress = Math.round(Math.min(100, Math.max(0, progress)));
        await supabase
          .from('tasks')
          .update({ progress: cappedProgress })
          .eq('id', taskId);

        if (resolvedGoalId) {
          await recalculateGoalProgress(resolvedGoalId);
        }
      }

      return data;
    } else {
      // Update existing shared task history
      let updatedTaskHistory = { ...existingHistory };

      // Ensure progressHistory is initialized as an array
      if (!Array.isArray(updatedTaskHistory.progressHistory)) {
        updatedTaskHistory.progressHistory = [];
      }

      const targetDateKey = toLocalISOString(targetDate);
      const targetEntryIndex = updatedTaskHistory.progressHistory.findIndex(t =>
        toLocalISOString(ensureDate(t.progressDate)) === targetDateKey
      );

      if (markedAsCompleted === false) {
        if (targetEntryIndex >= 0) {
          updatedTaskHistory.progressHistory = updatedTaskHistory.progressHistory.filter((_, idx) => idx !== targetEntryIndex);
        }
      } else if (targetEntryIndex >= 0) {
        updatedTaskHistory.progressHistory = updatedTaskHistory.progressHistory.map((t, idx) =>
          idx === targetEntryIndex
            ? {
              ...t,
              progress: (progress !== null && progress !== undefined) ? Math.round(Math.min(100, Math.max(0, progress))) : t.progress,
              updatedOn: toLocalISOString(targetDate),
              markedAsCompleted: markedAsCompleted !== null
                ? markedAsCompleted
                : t.markedAsCompleted
            }
            : t
        );
      } else {
        const isActuallyCompleted = markedAsCompleted !== null
          ? markedAsCompleted
          : (progress !== null && progress !== undefined)
            ? Math.round(Math.min(100, Math.max(0, progress))) > 0
            : false;

        if (isActuallyCompleted || isRecurring) {
          if (isRecurring && markedAsCompleted === false) {
            // Skip
          } else {
            const newEntry = {
              progressDate: targetDateKey,
              progress: (progress !== null && progress !== undefined) ? Math.round(Math.min(100, Math.max(0, progress))) : 0,
              updatedOn: targetDateKey,
              markedAsCompleted: markedAsCompleted !== null
                ? markedAsCompleted
                : false,
              updatedBy: userEmail
            };
            updatedTaskHistory.progressHistory = [...updatedTaskHistory.progressHistory, newEntry];
          }
        }
      }

      if (comment && comment.trim()) {
        if (!updatedTaskHistory.commentHistory) {
          updatedTaskHistory.commentHistory = [];
        }

        const targetCommentEntry = updatedTaskHistory.commentHistory.find(entry =>
          new Date(entry.commentDate).toDateString() === targetDate.toDateString()
        );

        if (targetCommentEntry) {
          targetCommentEntry.comments.push(comment.trim());
          targetCommentEntry.commentedBy = userEmail;
        } else {
          updatedTaskHistory.commentHistory.push({
            comments: [comment.trim()],
            commentDate: targetDate.toDateString(),
            commentedBy: userEmail
          });
        }
      }


      const { data, error } = await supabase
        .from('task_history')
        .update({
          progressHistory: updatedTaskHistory.progressHistory,
          commentHistory: updatedTaskHistory.commentHistory
        })
        .eq('taskId', taskId)
        .select()
        .single();

      if (error) {
        console.error('Error updating task history:', error);
        throw error;
      }

      // Update the shared task's progress field and recalculate goal progress
      // Fetch the task and goal info to determine recurrence and goalId
      const { data: taskData } = await supabase
        .from('tasks')
        .select('id, goalId, recurrence, startDate, endDate')
        .eq('id', taskId)
        .single();

      if (taskData) {
        let finalProgress = null;

        // Calculate progress from history
        if (taskData.recurrence && taskData.recurrence.pattern !== 'none') {
          const { calculateRecurringTaskProgress } = require('../utils/recurringTasks');
          const calculated = calculateRecurringTaskProgress({
            ...taskData,
            progressHistory: updatedTaskHistory.progressHistory
          });
          finalProgress = calculated.progress;
        } else {
          // For non-recurring tasks, sum all progress history entries
          finalProgress = updatedTaskHistory.progressHistory.reduce((sum, entry) => sum + (entry.progress || 0), 0);
        }

        if (finalProgress !== null && finalProgress !== undefined) {
          const cappedProgress = Math.round(Math.min(100, Math.max(0, finalProgress)));
          await supabase
            .from('tasks')
            .update({ progress: cappedProgress })
            .eq('id', taskId);

          // Recalculate parent goal progress
          if (taskData.goalId) {
            await recalculateGoalProgress(taskData.goalId);
          }
        }
      }

      return data;
    }
  } catch (error) {
    console.error('Error creating/updating task history:', error);
    throw error;
  }
};

export const getTaskHistory = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('task_history')
      .select('*')
      .eq('taskId', taskId)
      .single();

    if (error && error.code !== 'PGRST116') {
      throw error;
    }

    return data || null;
  } catch (error) {
    console.error('Error fetching task history:', error);
    throw error;
  }
};

export const migrateTaskHistory = async (taskId) => {
  try {
    // Get the task with its completionHistory
    const { data: task, error: taskError } = await supabase
      .from('tasks')
      .select('*')
      .eq('id', taskId)
      .single();

    if (taskError) throw taskError;
    if (!task.completionHistory) return null;

    // Check if task history already exists
    const existingHistory = await getTaskHistory(taskId);
    if (existingHistory) return existingHistory;

    // Parse completionHistory
    let completionHistory = task.completionHistory;
    if (typeof completionHistory === 'string') {
      try {
        completionHistory = JSON.parse(completionHistory);
      } catch {
        return null;
      }
    }

    // Convert to new format
    const progressHistory = [];
    const commentHistory = [];
    const commentMap = new Map();

    if (Array.isArray(completionHistory)) {
      // New array format
      completionHistory.forEach(entry => {
        if (entry.progress !== null && entry.progress !== undefined) {
          const cappedProgress = Math.round(Math.min(100, Math.max(0, entry.progress)));
          progressHistory.push({
            progress: cappedProgress,
            updatedOn: entry.timestamp,
            progressDate: entry.timestamp,
            markedAsCompleted: cappedProgress >= 100
          });
        }

        if (entry.comment && entry.comment.trim()) {
          const date = new Date(entry.timestamp).toDateString();
          if (!commentMap.has(date)) {
            commentMap.set(date, []);
          }
          commentMap.get(date).push(entry.comment.trim());
        }
      });
    } else if (completionHistory && typeof completionHistory === 'object') {
      // Old object format - extract what we can
      if (completionHistory.achieved && completionHistory.achieved.lastUpdated) {
        progressHistory.push({
          progress: 100,
          updatedOn: completionHistory.achieved.lastUpdated,
          progressDate: completionHistory.achieved.lastUpdated,
          markedAsCompleted: true
        });
      }
    }

    // Convert comment map to comment history format
    commentMap.forEach((comments, date) => {
      commentHistory.push({
        comments,
        commentDate: date
      });
    });

    // Try to resolve goalId - it's required for task_history
    let resolvedGoalId = task.goalId;

    // If goalId is missing, try to fetch it from the tasks table
    if (!resolvedGoalId) {
      // Try to find goalId through goals table by querying tasks relationship
      const { data: goalsData, error: goalError } = await supabase
        .from('goals')
        .select('id, tasks!inner(id)')
        .eq('tasks.id', taskId)
        .limit(1)
        .maybeSingle();

      if (!goalError && goalsData?.id) {
        resolvedGoalId = goalsData.id;
        // Update the task with the found goalId
        await supabase
          .from('tasks')
          .update({ goalId: resolvedGoalId })
          .eq('id', taskId);
      }
    }

    // If we still don't have a goalId, log warning and skip task_history creation
    if (!resolvedGoalId) {
      console.warn(`Task ${taskId} does not have a goalId. Skipping task_history creation during migration.`);
      return null; // Return null instead of throwing to allow graceful handling
    }

    // Create task history record
    const { data, error } = await supabase
      .from('task_history')
      .insert({
        taskId,
        userId: task.userId,
        goalId: resolvedGoalId,
        progressHistory,
        commentHistory,
        progressVersion: []
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error migrating task history:', error);
    throw error;
  }
};

export const updateTaskHistoryComment = async (taskId, commentDate, commentIndex, newComment, commentedBy = null) => {
  try {
    const taskHistory = await getTaskHistory(taskId);
    if (!taskHistory) throw new Error('Task history not found');

    let commentHistory = taskHistory.commentHistory || [];

    // Use the same date comparison logic as the component
    // commentDate is YYYY-MM-DD string from component
    // entry.commentDate can be ISO string, Date object, or date string
    const targetDateObj = toLocalDate(commentDate);
    const targetDateStr = targetDateObj ? targetDateObj.toDateString() : '';

    if (!targetDateStr) {
      throw new Error(`Invalid date format: ${commentDate}`);
    }

    // Find the comment entry for the given date and commentedBy (if provided)
    // If commentedBy is provided, match both; otherwise match by date only (for backward compatibility)
    const commentEntry = commentHistory.find(entry => {
      if (!entry || !entry.commentDate) return false;
      const entryDate = toLocalDate(entry.commentDate);
      const entryDateStr = entryDate ? entryDate.toDateString() : '';
      const dateMatches = entryDateStr === targetDateStr && entryDateStr !== '';
      if (commentedBy) {
        return dateMatches && entry.commentedBy === commentedBy;
      }
      return dateMatches;
    });

    if (!commentEntry) {
      // Log for debugging
      console.error('Comment entry not found. Available dates:',
        commentHistory.map(e => {
          const entryDate = toLocalDate(e.commentDate);
          return {
            stored: e.commentDate,
            normalized: entryDate ? entryDate.toDateString() : 'invalid'
          };
        }),
        'Target:', { input: commentDate, normalized: targetDateStr }
      );
      throw new Error(`Comment entry not found for date: ${commentDate} (normalized: ${targetDateStr})`);
    }

    if (commentEntry.comments[commentIndex] === undefined) {
      throw new Error(`Comment index ${commentIndex} not found. Total comments: ${commentEntry.comments.length}`);
    }

    // Update the specific comment
    commentEntry.comments[commentIndex] = newComment.trim();

    // Remove empty comments
    commentEntry.comments = commentEntry.comments.filter(comment => comment.trim());

    // Remove the entire entry if no comments left
    commentHistory = commentHistory.filter(entry =>
      entry.comments && entry.comments.length > 0
    );

    const { data, error } = await supabase
      .from('task_history')
      .update({ commentHistory })
      .eq('taskId', taskId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating task history comment:', error);
    throw error;
  }
};

export const deleteTaskHistoryComment = async (taskId, commentDate, commentIndex, commentedBy = null) => {
  try {
    const taskHistory = await getTaskHistory(taskId);
    if (!taskHistory) throw new Error('Task history not found');

    let commentHistory = taskHistory.commentHistory || [];

    // Use the same date comparison logic as the component
    // commentDate is YYYY-MM-DD string from component
    // entry.commentDate can be ISO string, Date object, or date string
    const targetDateObj = toLocalDate(commentDate);
    const targetDateStr = targetDateObj ? targetDateObj.toDateString() : '';

    if (!targetDateStr) {
      throw new Error(`Invalid date format: ${commentDate}`);
    }

    // Find the comment entry for the given date and commentedBy (if provided)
    // If commentedBy is provided, match both; otherwise match by date only (for backward compatibility)
    const commentEntry = commentHistory.find(entry => {
      if (!entry || !entry.commentDate) return false;
      const entryDate = toLocalDate(entry.commentDate);
      const entryDateStr = entryDate ? entryDate.toDateString() : '';
      const dateMatches = entryDateStr === targetDateStr && entryDateStr !== '';
      if (commentedBy) {
        return dateMatches && entry.commentedBy === commentedBy;
      }
      return dateMatches;
    });

    if (!commentEntry) {
      // Log for debugging
      console.error('Comment entry not found for DELETE. Available entries:',
        commentHistory.map(e => {
          const entryDate = toLocalDate(e.commentDate);
          return {
            stored: e.commentDate,
            normalized: entryDate ? entryDate.toDateString() : 'invalid',
            commentedBy: e.commentedBy,
            commentsCount: e.comments?.length || 0
          };
        }),
        'Target:', { input: commentDate, normalized: targetDateStr, commentedBy }
      );
      throw new Error(`Comment entry not found for date: ${commentDate} (normalized: ${targetDateStr})${commentedBy ? ` and user: ${commentedBy}` : ''}`);
    }

    if (commentEntry.comments[commentIndex] === undefined) {
      console.error('Comment index out of bounds for DELETE:', {
        commentIndex,
        totalComments: commentEntry.comments.length,
        comments: commentEntry.comments,
        commentedBy: commentEntry.commentedBy
      });
      throw new Error(`Comment index ${commentIndex} not found. Total comments: ${commentEntry.comments.length}`);
    }

    // Remove the specific comment
    commentEntry.comments.splice(commentIndex, 1);

    // Remove the entire entry if no comments left
    commentHistory = commentHistory.filter(entry =>
      entry.comments && entry.comments.length > 0
    );

    const { data, error } = await supabase
      .from('task_history')
      .update({ commentHistory })
      .eq('taskId', taskId)
      .select()
      .single();

    if (error) throw error;
    // Return the updated data with commentHistory for consistency
    return { success: true, data, commentHistory };
  } catch (error) {
    console.error('Error deleting task history comment:', error);
    return { success: false, error: error.message };
  }
};

export const migrateAllTaskHistory = async (userId = null) => {
  try {

    // Get all tasks that have completionHistory but no task_history record
    let query = supabase
      .from('tasks')
      .select('*')
      .not('completionHistory', 'is', null);

    if (userId) {
      query = query.eq('userId', userId);
    }

    const { data: tasks, error: tasksError } = await query;
    if (tasksError) throw tasksError;


    let migratedCount = 0;
    let skippedCount = 0;

    for (const task of tasks) {
      try {
        // Check if task history already exists
        const existingHistory = await getTaskHistory(task.id);
        if (existingHistory) {
          skippedCount++;
          continue;
        }

        // Migrate this task's history
        await migrateTaskHistory(task.id);
        migratedCount++;

      } catch (error) {
        console.error(`Failed to migrate task ${task.id}:`, error);
      }
    }

    return { migratedCount, skippedCount, totalTasks: tasks.length };
  } catch (error) {
    console.error('Error in bulk migration:', error);
    throw error;
  }
};

export const fixGoalCategories = async (userId = null) => {
  try {

    // Get all goals with category data
    let query = supabase
      .from('goals')
      .select('id, title, category');

    if (userId) {
      query = query.eq('userId', userId);
    }

    const { data: goals, error: goalsError } = await query;
    if (goalsError) throw goalsError;


    let fixedCount = 0;
    let skippedCount = 0;

    for (const goal of goals) {
      try {
        let categoryValue = goal.category;

        // Check if category is a JSON string that needs to be parsed
        if (typeof categoryValue === 'string' && categoryValue.startsWith('{')) {
          try {
            const parsedCategory = JSON.parse(categoryValue);
            categoryValue = parsedCategory.value || parsedCategory.id || categoryValue;
          } catch (parseError) {
            console.warn(`Failed to parse category for goal ${goal.id}:`, parseError);
            continue;
          }
        }

        // Check if category is an object
        if (typeof categoryValue === 'object' && categoryValue !== null) {
          categoryValue = categoryValue.value || categoryValue.id || 'personal';
        }

        // Only update if the category value has changed
        if (categoryValue !== goal.category) {
          const { error: updateError } = await supabase
            .from('goals')
            .update({ category: categoryValue })
            .eq('id', goal.id);

          if (updateError) {
            console.error(`Failed to update goal ${goal.id}:`, updateError);
            continue;
          }

          fixedCount++;
        } else {
          skippedCount++;
        }
      } catch (error) {
        console.error(`Failed to process goal ${goal.id}:`, error);
      }
    }

    return { fixedCount, skippedCount, totalGoals: goals.length };
  } catch (error) {
    console.error('Error in goal category cleanup:', error);
    throw error;
  }
};



// Achievement functions
export const unlockAchievement = async (userId, achievementId, badgeId = null) => {
  try {
    // First check if achievement already exists
    const { data: existing } = await supabase
      .from('achievements')
      .select('*')
      .eq('userId', userId)
      .eq('achievementId', achievementId)
      .single();

    if (existing) {
      // Achievement already exists, just return it
      return existing;
    }

    // Insert new achievement
    const { data, error } = await supabase
      .from('achievements')
      .insert({
        userId,
        achievementId,
        badgeId,
        unlocked: true,
        unlockedAt: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error unlocking achievement:', error);
    throw error;
  }
};

export const getAchievements = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('achievements')
      .select('*')
      .eq('userId', userId)
      .eq('unlocked', true);

    if (error) {
      console.error('Error fetching achievements:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching achievements:', error);
    throw error;
  }
};

export const getRewards = async (userId) => {
  try {
    // Get completed challenges where user is the recipient (or child account)
    // A completed challenge is considered a "Reward"
    const { data, error } = await supabase
      .from('challenges')
      .select('*, goals(title)')
      .eq('status', 'completed')
      .or(`challengeTo.eq.${userId},childAccountId.eq.${userId}`)
      .order('completedAt', { ascending: false });

    if (error) throw error;

    // Enrich with sender info
    if (data && data.length > 0) {
      const enrichedRewards = await Promise.all(data.map(async (reward) => {
        let senderName = 'Unknown';

        if (reward.challengeFrom) {
          // Try to get sender name from profiles
          const { data: senderData } = await supabase
            .from('profiles')
            .select('displayName')
            .eq('id', reward.challengeFrom)
            .single();

          if (senderData) {
            senderName = senderData.displayName;
          } else {
            // Try child account
            const { data: childSender } = await supabase
              .from('child_accounts')
              .select('childName')
              .eq('id', reward.challengeFrom)
              .single();

            if (childSender) senderName = childSender.childName;
          }
        }

        return {
          ...reward,
          senderName
        };
      }));

      return enrichedRewards;
    }

    return [];
  } catch (error) {
    console.error('Error fetching rewards:', error);
    // Return empty array instead of throwing to avoid breaking the UI
    return [];
  }
};

// User Settings helper functions
export const getUserSettings = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching user settings:', error);
    return null;
  }
};

export const updateUserSettings = async (userId, updates) => {
  try {
    const { data, error } = await supabase
      .from('user_settings')
      .upsert({ user_id: userId, ...updates, updated_at: new Date().toISOString() })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error updating user settings:', error);
    throw error;
  }
};